import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Card, Chip, Icon, Input, Textarea } from "@/ds";
import api from "@/lib/api";
import ImageUploader from "@/components/uploads/ImageUploader";
import { useAuth } from "@/contexts/AuthContext";
import { INVITE_PROVIDER_ONBOARDING, armPushInvite } from "@/lib/push";
import { categoryLabel } from "@/lib/catalog";
import { anonymousKeyFor, executeAction, rejectAction } from "@/lib/dany";

/**
 * FixyBee setup, as one card in the conversation (DY2.2).
 *
 * The four steps of `/onboarding` — profile, phone OTP, categories, photo —
 * without the four screens. Same contract as every other Dany write: the
 * `action_id` is the authorization, Finish is the commit point, and the write
 * itself is `POST /users/me/onboarding` called server-side.
 *
 * **The phone is captured here and confirmed elsewhere** (REG, 2026-10-28).
 * The card used to send the SMS code and hold the box for it, because
 * `submit_onboarding` refused a FixyBee whose phone was not yet verified. That
 * refusal is gone (a confirmed phone is a mark on the public profile, not a
 * lock), so the card takes the number like the sign-up form does, and once
 * setup is saved it suggests `/provider/verify-phone` — the one place in the
 * product where a code is typed.
 */
export default function DanyProviderSetupWidget({ data, threadId, onCommitted }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [fields, setFields] = React.useState(() => ({
    ...(data?.draft || {}),
    service_categories: [...((data?.draft || {}).service_categories || [])],
  }));
  const [phase, setPhase] = React.useState(data?.saved ? "saved" : "draft");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  // The catalog, for adding a specialty Dany did not suggest.
  const [catalog, setCatalog] = React.useState([]);
  const [browsing, setBrowsing] = React.useState(false);

  const actionId = data?.action_id;

  React.useEffect(() => {
    if (!browsing || catalog.length) return;
    api.get("/catalog/categories")
      .then((r) => setCatalog(r.data.categories || []))
      .catch(() => setCatalog([]));
  }, [browsing, catalog.length]);

  const setPhone = (value) => {
    // Any edit invalidates a verification the profile already had: the server
    // drops it too, because the number it vouched for is not this one.
    setFields((current) => ({ ...current, phone: value, phone_verified: false }));
  };

  const toggleCategory = (categoryId) => {
    setFields((current) => {
      const has = current.service_categories.includes(categoryId);
      return {
        ...current,
        service_categories: has
          ? current.service_categories.filter((c) => c !== categoryId)
          : [...current.service_categories, categoryId],
        // A category dropped takes its specialties with it; the server would
        // drop them anyway (they are filtered against the kept categories) and
        // leaving them here would show ticks for a category nobody selected.
        service_subcategories: has ? [] : current.service_subcategories,
      };
    });
  };

  const localProblem = React.useMemo(() => {
    if (!String(fields.full_name || "").trim()) return t("dany.setup.errorName");
    if (String(fields.phone || "").replace(/\D/g, "").length < 10) return t("dany.setup.errorPhone");
    if (!(fields.service_categories || []).length) {
      return t("dany.setup.errorCategories");
    }
    return null;
  }, [fields, t]);

  const finish = async () => {
    if (!actionId) return;
    setBusy(true);
    setError(null);
    try {
      await executeAction(actionId, fields, {
        anonymousKey: anonymousKeyFor(threadId),
      });
      setPhase("saved");
      // The thread re-reads itself: this card is not the only thing on
      // screen that this press changed (T11 N38).
      onCommitted?.();
      // The FixyBee is now waiting for jobs in their area, which is the
      // sentence push exists for — the same moment `/onboarding` arms it.
      armPushInvite(INVITE_PROVIDER_ONBOARDING);
      // `ProtectedRoute` reads the cached profile, and until this lands it
      // still says the account has no role — which would send them straight
      // back to the form they just skipped (the DY2.3 lesson, same shape).
      try { await refresh(); } catch { /* setup is saved either way */ }
    } catch (err) {
      setError(
        t([`dany.setup.${err.code}`, `dany.errors.${err.code}`,
           "dany.setup.failed"], {
          defaultValue: t("dany.setup.failed"),
        })
      );
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await rejectAction(actionId, { anonymousKey: anonymousKeyFor(threadId) });
    } catch { /* the card goes away either way */ }
    setPhase("dismissed");
    // The thread re-reads itself: this card is not the only thing on
    // screen that this press changed (T11 N38).
    onCommitted?.();
    setBusy(false);
  };

  if (phase === "dismissed") return null;

  if (phase === "saved") {
    return (
      <Card variant="raised" padding="md" data-testid="dany-setup-saved">
        <p style={heading}>{t("dany.setup.savedTitle")}</p>
        <p style={body}>{t("dany.setup.savedBody")}</p>
        {/* The dashboard is the next place; the phone is a suggestion beside
            it — a mark clients trust, never a step that blocks (2026-10-28). */}
        {!fields.phone_verified && (
          <p style={{ ...body, marginTop: "var(--space-2)" }}>{t("signupX.phoneNudge")}</p>
        )}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-3)", marginTop: "var(--space-4)", alignItems: "center" }}>
          <Button variant="primary" size="sm" iconRight="arrow-right"
                  onClick={() => navigate("/provider/dashboard")}
                  data-testid="dany-setup-dashboard">
            {t("dany.setup.openDashboard")}
          </Button>
          {!fields.phone_verified && (
            <Button variant="secondary" size="sm"
                    onClick={() => navigate("/provider/verify-phone")}
                    data-testid="dany-setup-verify-phone">
              {t("signupX.gateCta")}
            </Button>
          )}
        </div>
      </Card>
    );
  }

  const suggested = (fields.service_categories || []).map((id, index) => ({
    category_id: id,
    name: (fields.service_category_names || [])[index] || id,
  }));

  return (
    <Card variant="raised" padding="md" data-testid="dany-setup">
      <span style={eyebrow}>
        <Icon name="briefcase" size={12} style={{ color: "var(--hive-ink)" }} />
        {t("dany.setup.header")}
      </span>
      <p style={body}>{t("dany.setup.body")}</p>

      <Field label={t("dany.setup.fieldName")}>
        <Input
          value={fields.full_name || ""} maxLength={120}
          onChange={(e) => setFields({ ...fields, full_name: e.target.value })}
          data-testid="dany-setup-name"
        />
      </Field>
      <Field label={t("dany.setup.fieldCompany")}>
        <Input
          value={fields.company_name || ""} maxLength={120}
          placeholder={t("dany.setup.fieldCompanyHint")}
          onChange={(e) => setFields({ ...fields, company_name: e.target.value })}
          data-testid="dany-setup-company"
        />
      </Field>
      <Field label={t("dany.setup.fieldBio")}>
        <Textarea
          value={fields.bio || ""} maxLength={1000} rows={3}
          onChange={(e) => setFields({ ...fields, bio: e.target.value })}
          data-testid="dany-setup-bio"
        />
      </Field>

      <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <Field label={t("dany.setup.fieldZip")}>
          <Input
            value={fields.zip_code || ""} maxLength={5} inputMode="numeric"
            onChange={(e) => setFields({
              ...fields, zip_code: e.target.value.replace(/\D/g, "").slice(0, 5),
            })}
            data-testid="dany-setup-zip"
          />
        </Field>
        <Field label={t("dany.setup.fieldCity")}>
          <Input
            value={fields.city || ""} maxLength={80}
            onChange={(e) => setFields({ ...fields, city: e.target.value })}
            data-testid="dany-setup-city"
          />
        </Field>
        <Field label={t("dany.setup.fieldState")}>
          <Input
            value={fields.state || ""} maxLength={2} placeholder="FL"
            onChange={(e) => setFields({
              ...fields, state: e.target.value.toUpperCase(),
            })}
            data-testid="dany-setup-state"
          />
        </Field>
      </div>

      {/* ---- the phone: the number only; the code comes after setup ---- */}
      <div style={{ marginTop: "var(--space-4)" }} data-testid="dany-setup-phone-block">
        <span style={fieldLabel}>{t("dany.setup.fieldPhone")}</span>
        <Input
          value={fields.phone || ""} maxLength={24} inputMode="tel" type="tel"
          autoComplete="tel-national" placeholder="+1 555 123 4567"
          onChange={(e) => setPhone(e.target.value)}
          data-testid="dany-setup-phone"
        />
        <p style={{ ...body, marginTop: "var(--space-2)", color: "var(--text-subtle)" }}>
          {t("signupX.phoneHint")}
        </p>
      </div>

      {/* ---- specialties ---- */}
      <div style={{ marginTop: "var(--space-4)" }}>
        <span style={fieldLabel}>{t("dany.setup.fieldCategories")}</span>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
          {suggested.map((category) => (
            <Chip key={category.category_id} icon="check" selected
                  title={t("dany.setup.removeCategory")}
                  onClick={() => toggleCategory(category.category_id)}
                  data-testid={`dany-setup-category-${category.category_id}`}>
              {categoryLabel(t, category)}
            </Chip>
          ))}
          <Chip icon="plus" onClick={() => setBrowsing((value) => !value)}
                data-testid="dany-setup-browse">
            {t("dany.setup.addCategory")}
          </Chip>
        </div>
        {browsing && (
          <div style={{
            marginTop: "var(--space-3)", display: "flex", flexWrap: "wrap",
            gap: "var(--space-2)",
          }} data-testid="dany-setup-catalog">
            {catalog
              .filter((c) => !(fields.service_categories || []).includes(c.category_id))
              .map((category) => (
                <Chip key={category.category_id} icon="plus"
                      onClick={() => {
                        setFields((current) => ({
                          ...current,
                          service_categories: [
                            ...current.service_categories, category.category_id,
                          ],
                          service_category_names: [
                            ...(current.service_category_names || []),
                            categoryLabel(t, category),
                          ],
                        }));
                      }}>
                  {categoryLabel(t, category)}
                </Chip>
              ))}
          </div>
        )}
      </div>

      {/* ---- logo / photo ---- */}
      <div style={{ marginTop: "var(--space-4)", maxWidth: 180 }}>
        <span style={fieldLabel}>{t("dany.setup.fieldPhoto")}</span>
        <ImageUploader
          value={fields.avatar_url || null}
          purpose="profile_photo"
          aspect="square"
          testid="dany-setup-photo"
          onUploaded={({ url }) => setFields({ ...fields, avatar_url: url })}
          onCleared={() => setFields({ ...fields, avatar_url: "" })}
        />
      </div>

      {(localProblem || error) && (
        <p style={{ ...body, color: error ? "var(--danger)" : "var(--text-muted)" }}
           data-testid="dany-setup-problem">{error || localProblem}</p>
      )}

      <div style={{
        marginTop: "var(--space-5)", display: "flex", gap: "var(--space-2)",
        flexWrap: "wrap",
      }}>
        <Button variant="primary" size="sm" iconRight="arrow-right"
                disabled={busy || !!localProblem || !actionId}
                onClick={finish} data-testid="dany-setup-finish">
          {busy ? t("dany.setup.finishing") : t("dany.setup.finish")}
        </Button>
        <Button variant="ghost" size="sm" icon="x" disabled={busy}
                onClick={dismiss} data-testid="dany-setup-skip">
          {t("dany.setup.skip")}
        </Button>
      </div>
      <p style={{ ...body, color: "var(--text-subtle)" }}>
        {t("dany.setup.classicHint")}{" "}
        <a href="/signup/provider" style={{ color: "var(--text-accent)" }}>
          {t("dany.setup.classicLink")}
        </a>
      </p>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginTop: "var(--space-3)", flex: "1 1 140px" }}>
      <span style={fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

const fieldLabel = {
  display: "block", marginBottom: 4, fontFamily: "var(--font-display)",
  fontSize: "var(--text-xs)", textTransform: "uppercase",
  letterSpacing: "var(--tracking-widest)", color: "var(--dany-meta)",
};

const eyebrow = {
  display: "inline-flex", alignItems: "center", gap: 6,
  fontFamily: "var(--font-display)", fontSize: "var(--text-xs)",
  textTransform: "uppercase", letterSpacing: "var(--tracking-widest)",
  color: "var(--dany-meta)",
};

const heading = {
  fontFamily: "var(--font-display)", fontWeight: "var(--weight-bold)",
  fontSize: "var(--text-lg)", color: "var(--text-body)",
};

const body = {
  marginTop: "var(--space-2)", fontSize: "var(--text-sm)",
  color: "var(--text-muted)", lineHeight: "var(--leading-relaxed)",
  maxWidth: "var(--measure-body)",
};
